import FormField from 'further-ui/components/FormField';
import BasicSelect from 'further-ui/components/BasicSelect';
import { Controller, useFormContext } from 'react-hook-form';
import { ConsiderationTypeLabels } from 'further-ui/labels';
import {
  ConsiderationAllocationLogic,
  ConsiderationType,
} from 'further-types/lp/realisation';
import RadioButtons from 'further-ui/components/RadioButtons';
import ProRataConsiderationForm from './ProRataConsiderationForm';
import ManualConsiderationForm from './ManualConsiderationForm';
import CurrencySelector from 'further-ui/components/CurrencySelector';
import { CurrencyCode, Vehicle } from 'further-types/lp/vehicle';
import { realisation as rules } from 'further-ui/validations';
import WithholdingTaxExemptTable from './WithholdingTaxExemptTable';
import AllocationsTableSection from './AllocationsTablesSection';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { RealisationFormState } from '../types';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { Column, Row } from 'further-ui/components/Layout';
import { Button } from '@mui/material';
import { useState } from 'react';

type Props = {
  index: number;
  currency: CurrencyCode;
  selectedVehicle: Vehicle;
};

const ConsiderationEntry: React.FC<Props> = ({
  index,
  currency,
  selectedVehicle,
}) => {
  const { control, watch, getValues, setValue } =
    useFormContext<rules.CreateRealisationFormValues>();

  const { state, setState } = useFormWithStateContext<
    rules.CreateRealisationFormValues,
    RealisationFormState
  >();

  const disableInputs = state.consideration[index].disableAllocationsInputs;
  const showAllocationsTable = state.consideration[index].showAllocationsTable;

  const considerationAllocationLogic = watch(
    `consideration.${index}.considerationAllocationLogic`,
  );

  const holdingClassRealisations = watch('holdingClassRealisation') || [];
  const withholdingTaxList = watch('investorWithholdingTaxList') || [];

  const { mutateAsync: generateAllocation } =
    api.lpRealisation.generateAllocation.useMutation(
      useHandleTrpcMutation()({ refetchType: 'none' }),
    );

  const [isGenerating, setIsGenerating] = useState(false);

  const hasWithholdingTax = getValues('holdingClassRealisation').some(
    (holdingClass) =>
      holdingClass.cashConsiderationAmounts?.withholdingTaxPayable ?? 0 > 0,
  );

  const selectedCurrency = watch(`consideration.${index}.currency`) ?? currency;

  const isProRata =
    considerationAllocationLogic === ConsiderationAllocationLogic.ProRata;

  const onGenerateAllocation = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    try {
      const {
        holdingClassInvestmentsConsiderationValues,
        investorAllocations,
        investorAllocationsTotals,
      } = await generateAllocation({
        holdingClassRealisations: holdingClassRealisations || [],
        withholdingTaxList: withholdingTaxList || [],
        vehicleId: selectedVehicle._id,
      });

      setValue(
        `consideration.${index}.investorAllocations`,
        investorAllocations,
      );
      setState(
        (prev) =>
          ({
            ...prev,
            consideration: prev.consideration.map((consideration, i) =>
              i === index
                ? {
                    ...consideration,
                    showAllocationsTable: true,
                    disableAllocationsInputs: true,
                    holdingClassInvestmentsConsiderationValues,
                    investorAllocationsTotals,
                  }
                : consideration,
            ),
          } as RealisationFormState),
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const onClearConsiderationForm = () => {
    setState({
      consideration: state.consideration.map(({ index: i, ...rest }) => ({
        ...rest,
        index: i,
        showAllocationsTable: false,
        disableAllocationsInputs: false,
      })),
    });
  };

  return (
    <Column spacing="lg">
      <FormField size="md" label="Consideration type">
        <Controller
          control={control}
          name={`consideration.${index}.considerationType`}
          render={({ field }) => (
            <BasicSelect
              {...field}
              fullWidth
              disabled={disableInputs || isGenerating}
              options={Object.entries(ConsiderationTypeLabels)
                .filter(([value]) => value !== ConsiderationType.Equity)
                .map(([value, label]) => ({
                  value: value as ConsiderationType,
                  label,
                }))}
            />
          )}
        />
      </FormField>
      <FormField size="md" label="Currency">
        <Controller
          name={`consideration.${index}.currency`}
          control={control}
          defaultValue={currency}
          render={({ field: { ref, ...rest }, fieldState: { error } }) => (
            <CurrencySelector
              disabled={disableInputs || isGenerating}
              {...rest}
              error={error ? { message: error.message } : undefined}
              placeholder="Select currency"
            />
          )}
        />
      </FormField>
      <FormField label="Consideration allocation rule">
        <Controller
          control={control}
          name={`consideration.${index}.considerationAllocationLogic`}
          render={({ field }) => (
            <RadioButtons
              {...field}
              direction="row"
              disabled={disableInputs || isGenerating}
              options={[
                {
                  value: ConsiderationAllocationLogic.ProRata,
                  label:
                    'Enter total cash consideration and allocate pro-rata between holding classes',
                },
                {
                  value: ConsiderationAllocationLogic.Manual,
                  label: 'Manually enter cash consideration per holding class',
                },
              ]}
            />
          )}
        />
      </FormField>

      {isProRata ? (
        <ProRataConsiderationForm
          control={control}
          currencyCode={selectedCurrency}
          index={index}
          disabled={disableInputs || isGenerating}
        />
      ) : (
        <ManualConsiderationForm
          currencyCode={selectedCurrency}
          disabled={disableInputs || isGenerating}
        />
      )}
      {hasWithholdingTax && (
        <WithholdingTaxExemptTable
          currency={selectedCurrency}
          disabled={disableInputs || isGenerating}
        />
      )}
      <Row spacing="sm" fullWidth justify="space-between">
        <Button
          variant="outlined"
          onClick={onClearConsiderationForm}
          size="large"
          disabled={isGenerating}
        >
          Clear all
        </Button>
        <Button
          variant="contained"
          onClick={onGenerateAllocation}
          size="large"
          disabled={isGenerating}
          loading={isGenerating}
        >
          Generate allocation
        </Button>
      </Row>
      {showAllocationsTable &&
        state.consideration[index]
          .holdingClassInvestmentsConsiderationValues && (
          <AllocationsTableSection
            holdingClassInvestmentsConsiderationValues={
              state.consideration[index]
                .holdingClassInvestmentsConsiderationValues
            }
            investorAllocationsTotals={
              state.consideration[index].investorAllocationsTotals
            }
            currency={selectedCurrency}
            considerationIndex={index}
          />
        )}
    </Column>
  );
};

export default ConsiderationEntry;
